// Copyright (c) 2019 Celadon Development LLC, All rights reserved.
// Author Yana Sidorova <yana.sidorova@celadon.ae>
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import MediaQuery from 'react-responsive';
import { withStyles } from '@material-ui/core/styles';
import {
  Switch,
  Route,
  Link,
  Router,
} from 'react-router-dom';
import uuidv4 from 'uuid/v4';
import { connect } from 'react-redux';

import {
  MyProfile,
  Address,
  MyOrders,
  Payment,
  OrderFull,
  OrderNearestPostcode,
  ArrangeCollection,
  Vouchers,
  CustomerOverview,
  VoucherDetails,
} from '../components';
import IssueVoucherPage from '../../../../issue-voucher/container/IssueVoucherPage';
import VoucherConfirmedPage from '../../../../voucher-confirmed/container/VoucherConfirmedPage';
import RedeemVoucherPage from '../../../../redeem-voucher/container/RedeemVoucherPage';
import BookshopReportsPage from '../../../../bookshop-reports/container/BookshopReportsPage';
import * as basketActions from '../../../../basket/store/actions';
import history from '../../../../../services/history';
import { BOOKSHOP_STATUS_CHANGE, isBookshopAccount } from '../../../../../services/bookshopStatus';
import myprofile from '../../../../../images/account/myprofile.svg';
import address from '../../../../../images/account/address.svg';
import payment from '../../../../../images/account/payment.svg';
import myorders from '../../../../../images/account/myorders.svg';
import logout from '../../../../../images/account/logout.svg';
import voucher from '../../../../../images/account/voucher.svg';
import reports from '../../../../../images/account/reports.svg';
import styles from './styles';
import GlobalStyles from '../../../../../config/styles';
import * as getPaidActions from '../../../../get-paid/store/actions';

// Tabs every account sees.
const customerTabs = [{
  label: 'My profile',
  icon: myprofile,
  path: '/account',
  exact: true,
  component: <MyProfile />,
}, {
  label: 'Address',
  icon: address,
  exact: true,
  path: '/account/address',
  component: <Address />,
}, {
  label: 'Payment',
  icon: payment,
  exact: false,
  path: '/account/payment',
  component: <Payment />,
}, {
  label: 'My orders',
  icon: myorders,
  exact: true,
  path: '/account/orders',
  component: <MyOrders />,
}];

// Shop-only tabs, hidden from ordinary customers. Reports sits next to
// Vouchers because it reports on them.
const bookshopTabs = [{
  label: 'Vouchers',
  icon: voucher,
  path: '/account/vouchers',
  exact: true,
  component: <Vouchers />,
}, {
  label: 'Reports',
  icon: reports,
  path: '/account/reports',
  exact: true,
  component: <BookshopReportsPage />,
}];

// Last, so it stays on the end of the row however many tabs precede it.
const logoutTab = {
  label: 'Log out',
  exact: true,
  path: '/account/logout',
  icon: logout,
};

/**
 * The tabs for one account, built fresh rather than spliced into a shared
 * array.
 *
 * The previous version mutated a module-level array and guarded on its
 * length, which held together only while there was exactly one shop tab: two
 * would have been inserted and one removed, leaving a customer looking at a
 * Reports tab belonging to whoever was signed in before them.
 */
const buildTabs = isBookshop => [
  ...customerTabs,
  ...(isBookshop ? bookshopTabs : []),
  logoutTab,
];

const urlRedirect = (url) => {
  let path = url;
  const slashCount = (path.match(/\//g) || []).length;
  if (slashCount === 3) {
    const index = path.lastIndexOf('/');
    path = path.substring(0, index);
  }
  return path;
};

class AccountTab extends Component {
  state = {
    value: urlRedirect(history.location.pathname),
    // Held in state rather than read once at mount, so an account promoted
    // (or demoted) while signed in gains or loses its shop tabs where it
    // stands, without a fresh login.
    isBookshop: isBookshopAccount(),
  };

  static propTypes = {
    classes: PropTypes.object,
    resetBasket: PropTypes.func,
    resetActiveDiscountCode: PropTypes.func,
  };

  static defaultProps = {
    classes: {},
    resetBasket: () => { },
    resetActiveDiscountCode: () => { },
  };

  componentDidMount() {
    window.addEventListener(BOOKSHOP_STATUS_CHANGE, this.onBookshopStatusChange);
  }

  componentWillUnmount() {
    window.removeEventListener(BOOKSHOP_STATUS_CHANGE, this.onBookshopStatusChange);
  }

  onBookshopStatusChange = () => {
    const isBookshop = isBookshopAccount();

    this.setState({ isBookshop });

    // An account that has just lost its shop status may be standing on a tab
    // that no longer exists, which would otherwise leave an empty panel and
    // no way back except the browser's own navigation.
    if (!isBookshop && bookshopTabs.some(tab => history.location.pathname.startsWith(tab.path))) {
      history.push('/account');
    }
  }

  handleChange = (event, value) => {
    this.setState({ value });
  }

  onLogout = () => {
    localStorage.removeItem('loggedID');
    localStorage.removeItem('CartID');
    localStorage.removeItem('token');
    localStorage.removeItem('TradeCustomer');
    localStorage.removeItem('PaymentTypeForTradeCustomer');
    localStorage.removeItem('total');
    localStorage.removeItem('isBookshop');
    localStorage.setItem('wasQualified', 0);

    this.props.resetActiveDiscountCode();
    this.props.resetBasket();
    history.push('/');
  }

  renderTabs = (isMobile, location) => {
    const { classes } = this.props;
    const tabs = buildTabs(this.state.isBookshop);
    console.log(classes, 'classes');
    const { value } = this.state;
    const tabsProps = {
      onChange: this.handleChange,
      scrollButtons: 'on',
      classes: { indicator: classes.Indicator, flexContainer: classes.flexContainer },
      className: classes.tabs,
    };
    const path = urlRedirect(location.pathname);
    return (
      <Tabs {...tabsProps} value={path}>
        {tabs.map((el) => {
          const props = {
            disableRipple: true,
            className: classes.tab,
            value: el.path,
            component: Link,
            to: el.path,
            key: uuidv4(),
            icon: <img className={classes.iconHeight} src={el.icon} alt={'icon'} />,
          };
          if (!isMobile) props.label = el.label;
          return (
            <Tab
              {...props}
              style={value === el.path
                ? { color: GlobalStyles.color.COLOR_YELLOW_SELECT }
                : { color: GlobalStyles.color.COLOR_GREY }}
              icon={<img className={classes.iconHeight} src={el.icon} alt={'icon'} />}
            />);
        })}
      </Tabs>);
  }

  render() {
    const { classes } = this.props;
    const { value, isBookshop } = this.state;
    const tabs = buildTabs(isBookshop);

    if (value === '/account/logout') {
      this.onLogout();
    }

    // Shares the app's history instead of a nested <BrowserRouter>. A
    // BrowserRouter builds its own history, and a push on the app's history
    // does not raise popstate - so this router never saw programmatic
    // navigation and kept rendering the previous screen while the URL moved
    // on. Harmless while every voucher screen was a top-level route; once
    // they moved under /account it left the URL and the tree disagreeing.
    return (
      <Router history={history}>
        <div className={classes.root}>
          <Route
            path={'/account'}
            render={({ location }) => (
              <AppBar className={classes.appBar} position="static">
                <MediaQuery minWidth={650}>
                  {matches => (matches
                    ? (this.renderTabs(false, location))
                    : this.renderTabs(true, location))}
                </MediaQuery>
                <Switch>
                  {tabs.map(el => (
                    <Route
                      key={uuidv4()}
                      exact={el.exact}
                      path={el.path}
                      render={() => el.component}
                    />))}
                  <Route
                    path={'/account/orders/full-order/:id'}
                    component={OrderFull}
                  />
                  <Route
                    path={'/account/orders/nearest-postcode/:id'}
                    component={OrderNearestPostcode}
                  />
                  <Route
                    path={'/account/orders/arrange-collection/:id'}
                    component={ArrangeCollection}
                  />
                  {/* Issuing and confirming live under /account so the shop
                      keeps these tabs on every voucher screen - the client
                      asked for the navigation to run throughout rather than a
                      dead-end 'Continue' button. */}
                  <Route
                    path={'/account/vouchers/issue'}
                    component={IssueVoucherPage}
                  />
                  <Route
                    path={'/account/vouchers/confirmed'}
                    component={VoucherConfirmedPage}
                  />
                  <Route
                    path={'/account/vouchers/redeem'}
                    component={RedeemVoucherPage}
                  />
                  <Route
                    path={'/account/vouchers/customer-overview/:id'}
                    component={CustomerOverview}
                  />
                  <Route
                    path={'/account/vouchers/voucher/:id'}
                    component={VoucherDetails}
                  />
                </Switch>
              </AppBar>)}
          />
        </div>
      </Router>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    resetBasket: () => dispatch(basketActions.basketReset()),
    resetActiveDiscountCode: () => dispatch(getPaidActions.resetActiveDiscountCode()),
  };
};

export default connect(
  null,
  mapDispatchToProps
)(withStyles(styles)(AccountTab));
